import { Children, cloneElement, DragEvent, FC, HTMLAttributes, isValidElement, ReactElement } from 'react';

import DropPlaceholder from '@/shared/components/drag-and-drop/DropPlaceholder';

import { useNativeNestedResourceTabDrag } from './context';

interface SortableTabElementProps extends HTMLAttributes<HTMLDivElement> {
    'data-platform-sortable-tab-group'?: string;
    'data-platform-sortable-tab-id'?: string;
}

interface NativeSortableTabNodeProps {
    node: ReactElement<SortableTabElementProps>;
    resourceId: string;
}

/** Enhances the native tab in place; no drag registry can withhold its navigation node. */
const NativeSortableTabNode: FC<NativeSortableTabNodeProps> = ({ node, resourceId }) => {
    const drag = useNativeNestedResourceTabDrag();

    if (!drag) {
        return node;
    }

    const canDrop = Boolean(drag.activeResourceId && drag.allowReorder && !drag.parentLocked);
    const previewing = canDrop && Boolean(drag.dropTarget);
    const isPlaceholder = previewing && drag.activeResourceId === resourceId;
    const previewIndex = drag.previewResourceIds.indexOf(resourceId);
    const resolveEdge = (event: DragEvent<HTMLDivElement>) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const inLeadingHalf = event.clientX < rect.left + rect.width / 2;

        return inLeadingHalf !== drag.rtl ? 'before' : 'after';
    };
    const claimTarget = (event: DragEvent<HTMLDivElement>) => {
        if (!canDrop) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = 'move';

        // The placeholder follows the source node. Hovering that newly opened
        // slot must retain its destination, not send it back to the source index.
        if (resourceId !== drag.activeResourceId || !drag.dropTarget) {
            drag.setDropTarget({ edge: resolveEdge(event), resourceId });
        }
    };

    return cloneElement(node, {
        'data-platform-sortable-tab-group': drag.droppableId,
        'data-platform-sortable-tab-id': resourceId,
        onDragEnter: claimTarget,
        onDragOver: claimTarget,
        onDragLeave: (event) => {
            const next = event.relatedTarget;
            const nextGroup =
                next instanceof Element
                    ? next
                          .closest('[data-platform-sortable-tab-group]')
                          ?.getAttribute('data-platform-sortable-tab-group')
                    : undefined;

            if (canDrop && nextGroup !== drag.droppableId) {
                drag.setDropTarget(undefined);
            }
        },
        onDrop: (event) => {
            if (!canDrop) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            drag.dropResource(resourceId, resolveEdge(event));
        },
        style: {
            ...node.props.style,
            // Negative orders keep all tabs ahead of native add/overflow controls.
            // The source's occupied flex slot becomes the shared destination marker.
            ...(previewing
                ? {
                      order: previewIndex - drag.resourceIds.length,
                      marginInlineStart: previewIndex === 0 ? 0 : drag.activeTabSize?.gutter,
                  }
                : {}),
            ...(isPlaceholder
                ? {
                      boxSizing: 'border-box',
                      height: drag.activeTabSize?.height,
                      minWidth: drag.activeTabSize?.width,
                      position: 'relative',
                      width: drag.activeTabSize?.width,
                  }
                : {}),
        },
        children: (
            <>
                {Children.map(node.props.children, (child) =>
                    isValidElement<HTMLAttributes<HTMLElement>>(child)
                        ? cloneElement(child, {
                              style: { ...child.props.style, ...(isPlaceholder ? { opacity: 0 } : {}) },
                          })
                        : child,
                )}
                {isPlaceholder ? <DropPlaceholder style={{ inset: 0, position: 'absolute' }} /> : null}
            </>
        ),
    });
};

export default NativeSortableTabNode;
